import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ETIQUETA_ESTADO_OT,
  ETIQUETA_TIPO_TRABAJO,
  TIPOS_TRABAJO,
  formatearMoneda,
  type EstadoOT,
  type SeccionInformeClave,
} from '@cmms/shared';
import { api, consulta, descargarArchivo } from '../servicios/api.js';
import { Aviso, Cargando, Etiqueta, Indicador, Vacio, mensajeError } from '../componentes/piezas.js';
import { SelectorFecha, SelectorListaMultiple, useTiposActivo, useUbicaciones, useUsuariosOpciones } from '../componentes/selectores.js';
import { CasillaVerificacion } from '../componentes/piezas.js';
import { notificarErrorGlobal } from '../componentes/notificaciones-globales.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

interface ReporteOrdenes {
  total: number;
  porEstado: { estado: EstadoOT; cantidad: number }[];
  porTecnico: { usuarioId: string; nombre: string; cantidad: number; completadas: number }[];
  vencidasActualmente: number;
}

interface ReporteCumplimientoPreventivo {
  total: number;
  completadasATiempo: number;
  completadasTarde: number;
  pendientesVencidas: number;
  pendientesEnPlazo: number;
  porcentajeCumplimiento: number;
}

interface ReporteCostosInventario {
  costoManoObra: number;
  costoOtrosConceptos: number;
  costoPartes: number;
  costoTotal: number;
  topPartes: { parteId: string; nombre: string; cantidad: number; costo: number }[];
}

interface ReporteFallasActivos {
  totalFallas: number;
  porActivo: { activoId: string; nombre: string; ubicacion: string | null; sede: string | null; cantidad: number }[];
  porTipoFalla: { nombre: string; cantidad: number }[];
}

/**
 * Reportes operativos (§15): todo se calcula al vuelo sobre las tablas ya
 * existentes de OT, procedimientos e inventario — no hay nada persistido ni
 * programado aquí. Por eso el rango de fechas es el único filtro: sin
 * elegirlo, el backend usa el mes calendario en curso.
 */
const SECCIONES_OPCIONES: { clave: SeccionInformeClave; etiqueta: string }[] = [
  { clave: 'resumen', etiqueta: 'Resumen ejecutivo' },
  { clave: 'ordenes', etiqueta: 'Órdenes de trabajo' },
  { clave: 'preventivo', etiqueta: 'Cumplimiento preventivo' },
  { clave: 'costos', etiqueta: 'Costos e inventario' },
  { clave: 'fallas', etiqueta: 'Fallas por activo' },
];

/** Panel del informe: qué secciones incluir y si comparar con el periodo anterior. */
function PanelInforme({ filtros }: { filtros: Record<string, unknown> }): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [secciones, setSecciones] = useState<Record<SeccionInformeClave, boolean>>({
    resumen: true,
    ordenes: true,
    preventivo: true,
    costos: true,
    fallas: true,
  });
  const [comparar, setComparar] = useState(true);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const alPulsar = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alPulsar);
    return () => document.removeEventListener('mousedown', alPulsar);
  }, [abierto]);

  const elegidas = SECCIONES_OPCIONES.filter((s) => secciones[s.clave]).map((s) => s.clave);

  async function descargar(): Promise<void> {
    setExportando(true);
    try {
      await descargarArchivo(
        `/reportes/informe/pdf${consulta({ ...filtros, secciones: elegidas.join(','), comparar: comparar || undefined })}`,
      );
      setAbierto(false);
    } catch (error) {
      notificarErrorGlobal(error instanceof Error ? error.message : 'No se pudo generar el informe.');
    } finally {
      setExportando(false);
    }
  }

  return (
    <div className="menu-desplegable" ref={contenedor} style={{ alignSelf: 'flex-start' }}>
      <button
        type="button"
        className="boton secundario"
        aria-haspopup="dialog"
        aria-expanded={abierto}
        disabled={exportando}
        aria-busy={exportando}
        onClick={() => setAbierto((v) => !v)}
      >
        {exportando ? 'Generando informe…' : 'Exportar informe (PDF)'}
      </button>
      {abierto ? (
        <div className="menu-opciones-plantilla" role="dialog" aria-label="Opciones del informe" style={{ minWidth: 260, padding: 12 }}>
          <p className="campo-nota" style={{ margin: '0 0 6px' }}>Secciones</p>
          {SECCIONES_OPCIONES.map((s) => (
            <div key={s.clave} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <CasillaVerificacion
                marcada={secciones[s.clave]}
                alCambiar={() => setSecciones((prev) => ({ ...prev, [s.clave]: !prev[s.clave] }))}
                etiqueta={s.etiqueta}
              />
              <span>{s.etiqueta}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 4px', borderTop: '1px solid var(--borde)', marginTop: 8 }}>
            <CasillaVerificacion marcada={comparar} alCambiar={() => setComparar((v) => !v)} etiqueta="Comparar con el periodo anterior" />
            <span>Comparar con el periodo anterior</span>
          </div>
          <button
            type="button"
            className="boton primario pequeno"
            style={{ marginTop: 8, width: '100%' }}
            disabled={elegidas.length === 0 || exportando}
            onClick={() => void descargar()}
          >
            Descargar PDF
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Reportes(): JSX.Element {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [sede, setSede] = useState<string[]>([]);
  const [tipoActivo, setTipoActivo] = useState<string[]>([]);
  const [criticidad, setCriticidad] = useState<string[]>([]);
  const [tecnico, setTecnico] = useState<string[]>([]);
  const [tipoTrabajo, setTipoTrabajo] = useState<string[]>([]);

  const { data: ubicaciones } = useUbicaciones();
  const { data: tiposActivo } = useTiposActivo();
  const { data: usuarios } = useUsuariosOpciones();

  // Un solo objeto con los filtros: alimenta las consultas de la página y la descarga del PDF.
  const filtros = {
    fechaDesde: desde,
    fechaHasta: hasta,
    ubicacionId: sede,
    tipoActivoId: tipoActivo,
    criticidad,
    tecnicoId: tecnico,
    tipoTrabajo,
  };
  const parametros = consulta(filtros);
  const hayFiltros = Object.values(filtros).some((v) => (Array.isArray(v) ? v.length > 0 : v !== ''));

  const {
    data: ordenes,
    isLoading: cargandoOrdenes,
    isError: errorOrdenes,
    error: errorOrdenesDetalle,
  } = useQuery({
    queryKey: ['reportes', 'ordenes', parametros],
    queryFn: () => api.get<ReporteOrdenes>(`/reportes/ordenes${parametros}`),
  });

  const {
    data: preventivo,
    isLoading: cargandoPreventivo,
    isError: errorPreventivo,
    error: errorPreventivoDetalle,
  } = useQuery({
    queryKey: ['reportes', 'cumplimiento-preventivo', parametros],
    queryFn: () => api.get<ReporteCumplimientoPreventivo>(`/reportes/cumplimiento-preventivo${parametros}`),
  });

  const {
    data: costos,
    isLoading: cargandoCostos,
    isError: errorCostos,
    error: errorCostosDetalle,
  } = useQuery({
    queryKey: ['reportes', 'costos-inventario', parametros],
    queryFn: () => api.get<ReporteCostosInventario>(`/reportes/costos-inventario${parametros}`),
  });

  const {
    data: fallas,
    isLoading: cargandoFallas,
    isError: errorFallas,
    error: errorFallasDetalle,
  } = useQuery({
    queryKey: ['reportes', 'fallas-activos', parametros],
    queryFn: () => api.get<ReporteFallasActivos>(`/reportes/fallas-activos${parametros}`),
  });

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Reportes</h1>
          <p>Panorama operativo del periodo elegido — el mes en curso, si no se elige un rango.</p>
        </div>
        <PanelInforme filtros={filtros} />
      </div>

      <div className="filtros">
        <SelectorFecha id="reportes-desde" etiqueta="Desde" valor={desde} alCambiar={setDesde} />
        <SelectorFecha id="reportes-hasta" etiqueta="Hasta" valor={hasta} alCambiar={setHasta} />
        <SelectorListaMultiple
          id="reportes-sede"
          etiqueta="Sede"
          seleccion={sede}
          alCambiar={setSede}
          opciones={(ubicaciones ?? []).filter((u) => u.padreId === null).map((u) => ({ id: u.id, nombre: u.nombre }))}
        />
        <SelectorListaMultiple
          id="reportes-tipo-activo"
          etiqueta="Tipo de activo"
          seleccion={tipoActivo}
          alCambiar={setTipoActivo}
          opciones={(tiposActivo ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
        />
        <SelectorListaMultiple
          id="reportes-criticidad"
          etiqueta="Criticidad"
          seleccion={criticidad}
          alCambiar={setCriticidad}
          opciones={[
            { id: 'CRITICO', nombre: 'Crítico' },
            { id: 'IMPORTANTE', nombre: 'Importante' },
            { id: 'NORMAL', nombre: 'Normal' },
          ]}
        />
        <SelectorListaMultiple
          id="reportes-tecnico"
          etiqueta="Técnico"
          seleccion={tecnico}
          alCambiar={setTecnico}
          opciones={(usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
        />
        <SelectorListaMultiple
          id="reportes-tipo-trabajo"
          etiqueta="Tipo de trabajo"
          seleccion={tipoTrabajo}
          alCambiar={setTipoTrabajo}
          opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
        />
        {hayFiltros ? (
          <button
            type="button"
            className="boton secundario pequeno"
            style={{ alignSelf: 'flex-end' }}
            onClick={() => {
              setDesde('');
              setHasta('');
              setSede([]);
              setTipoActivo([]);
              setCriticidad([]);
              setTecnico([]);
              setTipoTrabajo([]);
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {/* ─── OTs por periodo / estado / técnico ────────────────────────────── */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-cabecera">
          <h2>Órdenes de trabajo</h2>
        </div>
        <div className="panel-cuerpo">
          {cargandoOrdenes ? <Cargando /> : null}
          {errorOrdenes ? (
            <Aviso tono="error">{mensajeError(errorOrdenesDetalle, 'No se pudo cargar el reporte de órdenes.')}</Aviso>
          ) : null}
          {ordenes ? (
            <>
              <div className="rejilla-indicadores">
                <Indicador tono="azul" titulo="ABIERTAS EN EL PERIODO" valor={ordenes.total} />
                <Indicador
                  tono={ordenes.vencidasActualmente > 0 ? 'roja' : 'verde'}
                  titulo="VENCIDAS AHORA"
                  valor={ordenes.vencidasActualmente}
                  nota={ordenes.vencidasActualmente > 0 ? 'Requieren atención' : 'Al día'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
                <div>
                  <div className="indicador-titulo" style={{ marginBottom: 8 }}>
                    POR ESTADO
                  </div>
                  {ordenes.porEstado.length === 0 ? (
                    <p style={{ color: 'var(--texto-tenue)', fontSize: 13 }}>Sin datos en el periodo.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ordenes.porEstado.map((e) => (
                        <div key={e.estado} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                          <span>{ETIQUETA_ESTADO_OT[e.estado]}</span>
                          <strong>{e.cantidad}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="indicador-titulo" style={{ marginBottom: 8 }}>
                    POR TÉCNICO
                  </div>
                  {ordenes.porTecnico.length === 0 ? (
                    <p style={{ color: 'var(--texto-tenue)', fontSize: 13 }}>Sin asignaciones individuales en el periodo.</p>
                  ) : (
                    <div className="tabla-contenedor">
                      <table className="tabla">
                        <thead>
                          <tr>
                            <th>Técnico</th>
                            <th>Asignadas</th>
                            <th>Completadas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordenes.porTecnico.map((t) => (
                            <tr key={t.usuarioId}>
                              <td>{t.nombre}</td>
                              <td>{t.cantidad}</td>
                              <td>{t.completadas}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* ─── Cumplimiento de mantenimiento preventivo ──────────────────────── */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-cabecera">
          <h2>Cumplimiento de mantenimiento preventivo</h2>
        </div>
        <div className="panel-cuerpo">
          {cargandoPreventivo ? <Cargando /> : null}
          {errorPreventivo ? (
            <Aviso tono="error">{mensajeError(errorPreventivoDetalle, 'No se pudo cargar el cumplimiento preventivo.')}</Aviso>
          ) : null}
          {preventivo && preventivo.total === 0 ? (
            <Vacio titulo="Sin OT preventivas con vencimiento en el periodo" />
          ) : null}
          {preventivo && preventivo.total > 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Etiqueta tono={preventivo.porcentajeCumplimiento >= 80 ? 'verde' : preventivo.porcentajeCumplimiento >= 50 ? 'ambar' : 'roja'}>
                  {preventivo.porcentajeCumplimiento}% a tiempo
                </Etiqueta>
                <span style={{ fontSize: 12.5, color: 'var(--texto-tenue)' }}>
                  Sobre {preventivo.total} OT preventiva{preventivo.total === 1 ? '' : 's'} con vencimiento en el periodo.
                </span>
              </div>
              <div className="rejilla-indicadores">
                <Indicador tono="verde" titulo="COMPLETADAS A TIEMPO" valor={preventivo.completadasATiempo} />
                <Indicador tono="ambar" titulo="COMPLETADAS TARDE" valor={preventivo.completadasTarde} />
                <Indicador tono="roja" titulo="PENDIENTES VENCIDAS" valor={preventivo.pendientesVencidas} />
                <Indicador tono="azul" titulo="PENDIENTES EN PLAZO" valor={preventivo.pendientesEnPlazo} />
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* ─── Costos e inventario ────────────────────────────────────────────── */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-cabecera">
          <h2>Costos e inventario</h2>
        </div>
        <div className="panel-cuerpo">
          {cargandoCostos ? <Cargando /> : null}
          {errorCostos ? (
            <Aviso tono="error">{mensajeError(errorCostosDetalle, 'No se pudo cargar el reporte de costos.')}</Aviso>
          ) : null}
          {costos ? (
            <>
              <div className="rejilla-indicadores">
                <Indicador tono="azul" titulo="MANO DE OBRA" valor={formatearMoneda(costos.costoManoObra)} />
                <Indicador tono="gris" titulo="OTROS CONCEPTOS" valor={formatearMoneda(costos.costoOtrosConceptos)} />
                <Indicador tono="ambar" titulo="PARTES CONSUMIDAS" valor={formatearMoneda(costos.costoPartes)} />
                <Indicador tono="azul" titulo="TOTAL DEL PERIODO" valor={formatearMoneda(costos.costoTotal)} />
              </div>

              {costos.topPartes.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                  <div className="indicador-titulo" style={{ marginBottom: 8 }}>
                    PARTES CON MAYOR CONSUMO
                  </div>
                  <div className="tabla-contenedor">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th>Parte</th>
                          <th>Cantidad consumida</th>
                          <th>Costo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costos.topPartes.map((p) => (
                          <tr key={p.parteId}>
                            <td>{p.nombre}</td>
                            <td>{p.cantidad}</td>
                            <td>{formatearMoneda(p.costo)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* ─── Qué tanto se dañan los activos ─────────────────────────────────── */}
      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-cabecera">
          <h2>Fallas por activo</h2>
        </div>
        <div className="panel-cuerpo">
          {cargandoFallas ? <Cargando /> : null}
          {errorFallas ? (
            <Aviso tono="error">{mensajeError(errorFallasDetalle, 'No se pudo cargar el reporte de fallas.')}</Aviso>
          ) : null}
          {fallas && fallas.totalFallas === 0 ? (
            <Vacio titulo="Sin OT reactivas en el periodo" detalle="Buena señal: no hubo fallas registradas." />
          ) : null}
          {fallas && fallas.totalFallas > 0 ? (
            <>
              <div className="rejilla-indicadores">
                <Indicador tono={fallas.totalFallas > 0 ? 'roja' : 'verde'} titulo="OT REACTIVAS EN EL PERIODO" valor={fallas.totalFallas} />
                <Indicador
                  tono="roja"
                  titulo="ACTIVO MÁS PROBLEMÁTICO"
                  valor={fallas.porActivo[0]?.nombre ?? '—'}
                  nota={fallas.porActivo[0] ? `${fallas.porActivo[0].cantidad} fallas` : undefined}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
                <div>
                  <div className="indicador-titulo" style={{ marginBottom: 8 }}>
                    ACTIVOS CON MÁS FALLAS
                  </div>
                  <div className="tabla-contenedor">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th>Activo</th>
                          <th>Ubicación</th>
                          <th>Sede</th>
                          <th>Fallas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fallas.porActivo.map((a) => (
                          <tr key={a.activoId}>
                            <td>
                              <EnlaceRuta to={`/activos/${a.activoId}`}>{a.nombre}</EnlaceRuta>
                            </td>
                            <td>{a.ubicacion ?? '—'}</td>
                            <td>{a.sede ?? '—'}</td>
                            <td>{a.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <div className="indicador-titulo" style={{ marginBottom: 8 }}>
                    TIPOS DE FALLA MÁS FRECUENTES
                  </div>
                  {fallas.porTipoFalla.length === 0 ? (
                    <p style={{ color: 'var(--texto-tenue)', fontSize: 13 }}>
                      Las OT reactivas del periodo no tienen tipo de falla asignado.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {fallas.porTipoFalla.map((t) => (
                        <div key={t.nombre} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                          <span>{t.nombre}</span>
                          <strong>{t.cantidad}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
